import { Box, Button, Card, CardContent, Container, Typography } from '@mui/material'
import { Header } from '@tom-ui/ui'
import { PATHS, withAuthenticationRequired } from '@tom-ui/utils'
import { useTranslate } from '@tolgee/react'
import { FC, ReactNode } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import {
  ContainerPinballPreview,
  DonkeyStackerPreview,
  InventoryDronePreview,
  PortDefensePreview,
  ReachStackerRunnerPreview,
  TerminalCrossingPreview,
  VesselInvadersPreview,
  YardHaulPreview,
  YardPacmanPreview,
} from './GamePreviews'

interface ArcadeGame {
  title: string
  description: string
  url: string
  accent: string
  preview: ReactNode
}

const Arcade: FC = withAuthenticationRequired(() => {
  const { t } = useTranslate()
  const navigate = useNavigate()

  const games: ArcadeGame[] = [
    {
      title: t('portDefense', 'Port Defense'),
      description: t(
        'portDefenseDescription',
        'Defend the terminal from incoming vessels and falling debris.',
      ),
      url: PATHS.portDefense.url,
      accent: '#3a8fd6',
      preview: <PortDefensePreview />,
    },
    {
      title: t('reachStackerRunner', 'Reach Stacker Runner'),
      description: t(
        'reachStackerRunnerDescription',
        'Race the dusk yard, dodge obstacles and stack containers.',
      ),
      url: PATHS.reachStackerRunner.url,
      accent: '#d97a2c',
      preview: <ReachStackerRunnerPreview />,
    },
    {
      title: t('yardHaul', 'Yard Haul'),
      description: t(
        'yardHaulDescription',
        'Snake-style hauling across the yard. Grow the trailer, deliver the load.',
      ),
      url: PATHS.yardHaul.url,
      accent: '#2ec27e',
      preview: <YardHaulPreview />,
    },
    {
      title: t('yardPacman', 'Yard Inspector'),
      description: t(
        'yardPacmanDescription',
        'Sweep every slot in the yard while ducking the patrol crew.',
      ),
      url: PATHS.yardPacman.url,
      accent: '#ffd166',
      preview: <YardPacmanPreview />,
    },
    {
      title: t('vesselInvaders', 'Vessel Invaders'),
      description: t(
        'vesselInvadersDescription',
        'Hold the terminal against waves of incoming carriers.',
      ),
      url: PATHS.vesselInvaders.url,
      accent: '#d94a8f',
      preview: <VesselInvadersPreview />,
    },
    {
      title: t('containerPinball', 'Container Pinball'),
      description: t(
        'containerPinballDescription',
        'Keep the container in play with two reach stacker flippers.',
      ),
      url: PATHS.containerPinball.url,
      accent: '#9b6dd6',
      preview: <ContainerPinballPreview />,
    },
    {
      title: t('donkeyStacker', 'Donkey Stacker'),
      description: t(
        'donkeyStackerDescription',
        'Climb the scaffolding past rolling containers and steam pipes to reach your reach stacker.',
      ),
      url: PATHS.donkeyStacker.url,
      accent: '#e6c14a',
      preview: <DonkeyStackerPreview />,
    },
    {
      title: t('terminalCrossing', 'Terminal Crossing'),
      description: t(
        'terminalCrossingDescription',
        'Hop the road, the rails and the harbor to load all five berths before time runs out.',
      ),
      url: PATHS.terminalCrossing.url,
      accent: '#3aa15a',
      preview: <TerminalCrossingPreview />,
    },
    {
      title: t('inventoryDrone', 'Inventory Drone'),
      description: t(
        'inventoryDroneDescription',
        'Thrust the rotors and thread your scanning drone between hanging cargo and ground stacks.',
      ),
      url: PATHS.inventoryDrone.url,
      accent: '#3a8fd6',
      preview: <InventoryDronePreview />,
    },
  ]

  return (
    <>
      <Helmet>
        <title>Arcade</title>
      </Helmet>
      <Header
        title={t('arcade', 'Arcade')}
        breadcrumbs={[
          { label: t('profile', 'Profile'), route: PATHS.profile.url },
          { label: t('arcade', 'Arcade') },
        ]}
      />
      <Container sx={{ paddingY: 3 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            gap: 2,
          }}
        >
          {games.map(game => (
            <Card
              key={game.url}
              variant='outlined'
              sx={{
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 3,
                },
              }}
            >
              <Box sx={{ height: 6, backgroundColor: game.accent }} />
              {game.preview}
              <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 1.5 }}>
                <Typography variant='h6'>{game.title}</Typography>
                <Typography variant='body2' color='text.secondary' sx={{ flexGrow: 1 }}>
                  {game.description}
                </Typography>
                <Button
                  variant='contained'
                  onClick={() => navigate(game.url)}
                  sx={{ alignSelf: 'flex-start', marginTop: 1 }}
                >
                  {t('play', 'Play')}
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Container>
    </>
  )
})

export default Arcade
